/**
 * FileUpload Component
 *
 * Reusable file upload component with drag & drop, preview, and validation
 * Supports images and PDFs
 */

import { useRef, useState } from 'preact/hooks';

interface FileUploadProps {
  id: string;
  label: string;
  accept: string;
  maxSize: number; // in MB
  required?: boolean;
  helpText?: string;
  onFileSelect: (file: File | null) => void;
  error?: string;
  fileType: 'image' | 'document';
}

export function FileUpload({
  id,
  label,
  accept,
  maxSize,
  required = false,
  helpText,
  onFileSelect,
  error,
  fileType,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return `El archivo excede el tamaño máximo de ${maxSize}MB`;
    }

    // Check file type
    const acceptedTypes = accept.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type;

    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type;
      }
      if (type.endsWith('/*')) {
        return mimeType.startsWith(type.replace('/*', ''));
      }
      return mimeType === type;
    });

    if (!isAccepted) {
      return `Tipo de archivo no permitido. Formatos aceptados: ${accept}`;
    }

    return null;
  };

  const handleFile = (file: File) => {
    setValidationError(null);

    console.log(`[FileUpload] File selected for "${label}":`, {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeKB: (file.size / 1024).toFixed(2) + ' KB',
      sizeMB: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
    });

    // Validate file
    const error = validateFile(file);
    if (error) {
      console.error(`[FileUpload] Validation failed for "${label}":`, error);
      setValidationError(error);
      onFileSelect(null);
      return;
    }

    console.log(`[FileUpload] File validated successfully for "${label}"`);
    setFileName(file.name);
    onFileSelect(file);

    // Generate preview for images
    if (fileType === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const handleFileInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer?.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleClear = () => {
    setFileName(null);
    setPreview(null);
    setValidationError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayError = error || validationError;

  return (
    <div class="space-y-2">
      {/* Label */}
      <label htmlFor={id} class="block text-sm font-medium text-gray-700">
        {label}
        {required && <span class="text-red-500 ml-1">*</span>}
      </label>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        id={id}
        name={id}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        class="hidden"
      />

      {/* Drop zone */}
      {!fileName && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          class={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${displayError ? 'border-red-300 bg-red-50' : ''}
          `}
        >
          <div class="space-y-2">
            {/* Upload icon */}
            <svg
              class={`mx-auto h-12 w-12 ${displayError ? 'text-red-400' : 'text-gray-400'}`}
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            {/* Upload text */}
            <div class="text-sm text-gray-600">
              <span class="font-medium text-blue-600 hover:text-blue-500">
                Haz clic para seleccionar
              </span>
              {' '}o arrastra el archivo aquí
            </div>

            {/* Help text */}
            {helpText && (
              <p class="text-xs text-gray-500">{helpText}</p>
            )}
          </div>
        </div>
      )}

      {/* Preview section */}
      {fileName && (
        <div class="border-2 border-gray-300 rounded-lg p-4">
          <div class="flex items-start space-x-4">
            {/* Image preview */}
            {preview && (
              <div class="flex-shrink-0">
                <img
                  src={preview}
                  alt="Preview"
                  class="h-20 w-20 object-cover rounded"
                />
              </div>
            )}

            {/* File icon for non-images */}
            {!preview && (
              <div class="flex-shrink-0">
                <svg
                  class="h-20 w-20 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            )}

            {/* File info */}
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">
                {fileName}
              </p>
              <p class="text-xs text-gray-500 mt-1">
                {helpText}
              </p>
            </div>

            {/* Remove button */}
            <button
              type="button"
              onClick={handleClear}
              class="flex-shrink-0 text-red-600 hover:text-red-800"
              title="Eliminar archivo"
            >
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {displayError && (
        <p class="text-sm text-red-600 mt-1">{displayError}</p>
      )}
    </div>
  );
}
